// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Lakko Contributors

/**
 * Member labels without a key fingerprint (docs/ux/client-redesign.md
 * section 6). The resolver (`resolver.ts`) falls back to `a1b2 c3d4` when a
 * person has no rename, no profile name and no verified username; that is
 * the right last resort for a contact DETAIL, where the safety number sits
 * next to it, and the wrong thing in a roster, a join request or a sender
 * line, where a hex group reads as an identifier the user is meant to
 * recognise. Those surfaces show a plain word instead.
 *
 * Pure: no wasm, no storage, no Solid.
 */
import { t } from "../l10n";
import type { ResolvedName } from "./resolver";

/** The generic label for a person the device cannot name. */
export const UNNAMED_MEMBER = (): string => t("chat.members.unnamed");

/** One-string surfaces (sender lines, typing, reactions, notices). */
export function memberNameOf(resolved: ResolvedName): string {
  return resolved.source === "fingerprint" ? UNNAMED_MEMBER() : resolved.name;
}

/** Roster rows: `Name · @username` when there is one, never a fingerprint. */
export function memberListLabelOf(resolved: ResolvedName): string {
  return resolved.source === "fingerprint" ? UNNAMED_MEMBER() : resolved.listLabel;
}

/** One roster entry, as the label mirror reads it. */
export interface LabelledMember {
  readonly signaturePublicKey: Uint8Array;
  /** ADR-0050 — the account the leaf is bound to, when known. */
  readonly accountKey?: Uint8Array | undefined;
}

/**
 * The names the app holds for each member of the given rosters and for each
 * further person key (`otherKeys`: contacts, a contact's retired keys), under
 * every key a background reader can be handed for that person: the account
 * key a leaf is bound to (the person key) and the leaf's own signature key.
 * `nameOf` is the app's own rule (the resolver); it answers `null` for a
 * person whose name the app does not know, and such a person is LEFT OUT: a
 * notification never carries a placeholder word. A key it throws on is left
 * out too. Pure.
 */
export function conversationLabelsOf(
  rosters: Iterable<readonly LabelledMember[]>,
  nameOf: (personKeyB64: string) => string | null,
  encode: (bytes: Uint8Array) => string,
  otherKeys: Iterable<string> = [],
): Map<string, string> {
  const out = new Map<string, string>();
  const named = (key: string): string | null => {
    try {
      const name = nameOf(key)?.trim() ?? "";
      return name.length > 0 ? name : null;
    } catch {
      return null;
    }
  };
  for (const roster of rosters) {
    for (const m of roster) {
      const person = encode(m.accountKey ?? m.signaturePublicKey);
      const name = named(person);
      if (name === null) continue;
      out.set(person, name);
      out.set(encode(m.signaturePublicKey), name);
    }
  }
  for (const key of otherKeys) {
    if (out.has(key)) continue;
    const name = named(key);
    if (name !== null) out.set(key, name);
  }
  return out;
}

/** What the viewer's own avatar is lettered from when it has no photo: their
 *  display name, the username only when there is none. */
export function ownAvatarLabel(displayName: string | null, username: string): string {
  const name = displayName?.trim() ?? "";
  if (name.length > 0) return name;
  return username.length > 0 ? username : "?";
}
